import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';

import {usePDPSearchParams} from '@salesforce/retail-react-app/app/hooks/use-pdp-search-params';
import {buildVariantValueHref, getVariantValueSwatch} from '@salesforce/retail-react-app/app/hooks/use-variation-attributes';
import {useVariationParams} from '@salesforce/retail-react-app/app/hooks/use-variation-params';
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils';

export const getAdditionalAttributeValueProps = (product, variationParams) => {
  const matchVariants = product.variants.filter(({variationValues}) => {
    return Object.keys(variationParams).every((key) => variationValues[key] === variationParams[key]);
  });

  const priceData = getPriceData(matchVariants.length > 1 ? {
    type: {
      master: true,
    },
    variants: matchVariants,
  } : matchVariants[0]);

  const orderable = matchVariants.some(({orderable}) => orderable);
  const isPopular = product.type?.variant && product.c_isPopular;

  const badges = {
    popular: isPopular,
  };

  return {orderable, priceData, badges};
};

/**
 * @override
 *  - Expose prices to variation attribute values
 *
 * Use a decorated version of a product variation attributes. This version
 * will have the following additions: which variation attribute is selected,
 * each value will have a product url, the swatch image if there is one, and
 * an updated orderable flag.
 *
 * @param {Object} product
 * @returns {Array} a decorated variation attributes list.
 *
 */
export const useVariationAttributes = (
  product = {},
  isProductPartOfSet = false,
  isProductPartOfBundle = false
) => {
  const {variationAttributes = []} = product;
  const location = useLocation();
  const variationParams = useVariationParams(product, isProductPartOfSet, isProductPartOfBundle);
  const existingParams = usePDPSearchParams(product.id);
  const isBundleChildVariant = isProductPartOfBundle && product?.type?.variant;

  // In the product bundle edit modal on the cart page, the variant ID of each bundle child is used as a key
  // for query parameters, so when a new variant is selected, a new query parameter is added since variants
  // have different IDs. The old one is not overwritten with existing logic so we remove it here
  if (isBundleChildVariant) {
    const [allParams] = existingParams;

    product?.variants?.forEach(({productId: variantId}) => {
      if (variantId !== product.id && allParams.get(variantId)) {
        allParams.delete(variantId);
      }
    });
  }

  return useMemo(() => variationAttributes.map((variationAttribute) => ({
    ...variationAttribute,
    selectedValue: {
      name: variationAttribute.values.find(
        ({value}) => value === variationParams?.[variationAttribute.id]
      )?.name,
      value: variationParams?.[variationAttribute.id],
    },
    values: variationAttribute.values.map((value) => {
      const params = {
        ...variationParams,
        [variationAttribute.id]: value.value,
      };

      const additionalProps = getAdditionalAttributeValueProps(product, params);

      return {
        ...value,
        image: getVariantValueSwatch(product, value),
        href: buildVariantValueHref({
          pathname: location.pathname,
          existingParams,
          newParams: params,
          productId: product.id,
          isProductPartOfSet,
          isProductPartOfBundle,
        }),
        ...additionalProps,
      };
    }),
  })), [location.search, product]);
};
